import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { SnAlarm } from '@lib/api/entities';
import { SnIntegrationsAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { FsFormatDatePipe } from '@lib/common/pipes';
import { hasCoordinates } from '@lib/geo/helpers';
import { LocaleService, t } from '@lib/translation';

import { AlprDetectionService, AlprDetails } from '../../../services/alpr/alpr-detection.service';
import { LprPlateImageDialogComponent } from '../../lpr-plate-image-dialog/lpr-plate-image-dialog.component';
import { AlprMediaCardComponent } from '../alpr-media-card/alpr-media-card.component';

@Component({
  selector: 'fs-alpr-hit-details-card',
  templateUrl: './alpr-hit-details-card.component.html',
  styleUrls: ['./alpr-hit-details-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlprHitDetailsCardComponent implements OnInit, OnChanges {
  @Input()
  public alarm: SnAlarm;

  @Input()
  public hideActions: boolean = false;

  @Output()
  public searchPlate = new EventEmitter<string>();

  @Output()
  public copyPlate = new EventEmitter<string>();

  @Output()
  public dockCamerasEvent = new EventEmitter<SnAlarm>();

  @Output()
  public goToAlarm = new EventEmitter<SnAlarm>();

  @Output()
  public closeAlarmEvent = new EventEmitter<SnAlarm>();

  @Output()
  public sendToEvent = new EventEmitter<SnAlarm>();

  // Reference to the media card component
  @ViewChild('mediaCard') mediaCard: AlprMediaCardComponent;

  public isFlipped = false;
  public autoDockMaxCameras: number;

  // Memoized ALPR details to avoid repeated service calls
  private _alprDetails: AlprDetails | null = null;

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _ability: SnAbilityService,
    private readonly _integrationsAPI: SnIntegrationsAPI,
    private readonly _alprDetection: AlprDetectionService,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _localeService: LocaleService,
  ) {
    this.autoDockMaxCameras = this._currentOrganization.settings.autoDockMaxCameras;
  }

  ngOnInit(): void {
    // Component initialization
    this._updateAlprDetails();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['alarm'] && !changes['alarm'].firstChange) {
      this._updateAlprDetails();
      this._cdr.markForCheck();
    }
  }

  /**
   * Updates the memoized ALPR details when alarm changes
   */
  private _updateAlprDetails(): void {
    if (this.alarm) {
      this._alprDetails = this._alprDetection.getAlprDetails(this.alarm);
    } else {
      this._alprDetails = null;
    }
  }

  /**
   * Gets the memoized ALPR details
   */
  private get alprDetails(): AlprDetails | null {
    return this._alprDetails;
  }

  get hotlistName(): string {
    return this._alprDetection.getHotlistName(this.alarm);
  }

  get hotlistCategory(): string {
    return this._alprDetection.getHotlistCategory(this.alarm);
  }

  get hasHotlistCategory(): boolean {
    const category = this.hotlistCategory;
    return !!(category && category.trim() !== '');
  }

  get hasHotlistName(): boolean {
    const name = this.hotlistName;
    return !!(name && name.trim() !== '' && !this._alprDetection.shouldHideHotlistName(name));
  }

  get hasHotlistData(): boolean {
    return this.hasHotlistCategory || this.hasHotlistName;
  }

  get plateState(): string {
    return this.alprDetails?.state?.toString() || t('alpr.fallbacks.notSpecified');
  }

  get vehicleColor(): string {
    return this.alprDetails?.color?.toString() || t('alpr.fallbacks.notAvailable');
  }

  get vehicleMake(): string {
    return this.alprDetails?.make?.toString() || t('alpr.fallbacks.notAvailable');
  }

  get vehicleModel(): string {
    return this.alprDetails?.model?.toString() || t('alpr.fallbacks.notAvailable');
  }

  get vehicleType(): string {
    return this.alprDetails?.type?.toString() || t('alpr.fallbacks.notAvailable');
  }

  get vehicleYear(): string {
    return this.alprDetails?.year?.toString() || t('alpr.fallbacks.notAvailable');
  }

  get cameraName(): string {
    return this.alprDetails?.cameraName || t('alpr.licensePlate.unknownCamera');
  }

  get cameraSource(): string {
    return this._alprDetection.getCameraSource(this.alarm);
  }

  get relativeTime(): string {
    if (!this.alarm?.updatedAt) return '';

    const now = new Date();
    const alertTime = new Date(this.alarm.updatedAt);
    const diffMs = now.getTime() - alertTime.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return t('alpr.timeFormats.justNow');
    if (diffMins < 60) return t('alpr.timeFormats.minutesAgo', { minutes: diffMins });

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24)
      return t('alpr.timeFormats.hoursAgo', { hours: diffHours, plural: diffHours > 1 ? 's' : '' });

    const diffDays = Math.floor(diffHours / 24);
    return t('alpr.timeFormats.daysAgo', { days: diffDays, plural: diffDays > 1 ? 's' : '' });
  }

  get absoluteTime(): string {
    if (!this.alarm?.updatedAt) return '';

    const alertTime = new Date(this.alarm.updatedAt);
    const formatDatePipe = new FsFormatDatePipe(this._localeService);

    // Format time: e.g., "10:31 am" (en-US), "10:31" (fr-FR)
    const timeString = formatDatePipe
      .transform(alertTime, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      } as any)
      .toLowerCase();

    // Format date: e.g., "7/25/25" (en-US), "25/07/25" (fr-FR)
    const dateString = formatDatePipe.transform(alertTime, {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    } as any);

    return t('alpr.templates.timeAndDate', { timeString, dateString });
  }

  public onSearchPlate(plateNumber: string): void {
    this.searchPlate.emit(plateNumber);
  }

  public onCopyPlate(plateNumber: string): void {
    this.copyPlate.emit(plateNumber);
  }

  // Menu Actions - Following alarm-card.component.ts pattern
  public hasImage(alarm: SnAlarm): boolean {
    return !!(alarm?.details?.thumbnailUrl || alarm?.details?.recordingUrl);
  }

  get isLprImage(): boolean {
    return !!(this.alarm?.details?.['Patch Image'] || this.alarm?.thumbnailUrl);
  }

  get hasMainImage(): boolean {
    return !!this.mainImageUrl;
  }

  get hasCoordinates(): boolean {
    return hasCoordinates(this.alarm.geometry.coordinates);
  }

  get mainImageUrl(): string | null {
    // Check various possible fields for the main context image
    const imageUrl =
      this.alarm?.thumbnailUrl ||
      this.alarm?.details?.thumbnailUrl ||
      this.alarm?.details?.contextImageUrl ||
      this.alarm?.details?.overviewImageUrl ||
      this.alarm?.details?.contextImage ||
      this.alarm?.details?.['Context Image'] ||
      this.alarm?.details?.['Overview Image'] ||
      null;

    return imageUrl;
  }

  public openEnlargedImageDialog(): void {
    this._matDialog.open(LprPlateImageDialogComponent, {
      data: this.alarm,
      width: '45vw',
      panelClass: 'lpr-plate__dialog-container',
    });
  }

  public turn(): void {
    this.isFlipped = !this.isFlipped;
    this.mediaCard.turn();
    this._cdr.markForCheck();
  }

  public showMetadata(): void {
    this.turn();
  }

  public openAlarmGrid(): void {
    window.open(`/camera-grids/dynamic/alarms/${this.alarm.id}`, '_blank');
  }

  public showOnMap(): void {
    this.goToAlarm.emit(this.alarm);
  }

  public dockCameras(): void {
    this.dockCamerasEvent.emit(this.alarm);
  }

  public openSendForm(alarm: SnAlarm): void {
    this.sendToEvent.emit(alarm);
  }

  public closeAlert(alarm: SnAlarm): void {
    this.closeAlarmEvent.emit(alarm);
  }

  /**
   * Get the dynamic title for the ALPR alert based on provider and priority
   */
  get alprTitle(): string {
    return this._alprDetection.getAlprTitle(this.alarm);
  }

  /**
   * Check if the alarm is from an Axon provider
   */
  public isAxonProvider(): boolean {
    return this._alprDetection.isAxonProvider(this.alarm);
  }

  /**
   * Extract the Axon provider type (Outpost, Fleet, Lightpost)
   */
  public getAxonProviderType(): string {
    return this._alprDetection.getAxonProviderType(this.alarm);
  }

  /**
   * Get the priority level from alarm details
   */
  public getPriorityLevel(): string {
    return this._alprDetection.getPriorityLevel(this.alarm);
  }
}
